import React, { useEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { getSupportedCurrencies } from '@/services/currency/currency-service';
import { TRADE_TYPE_LABELS } from '@/features/trades/constants/trade-labels';
import { useInventorySearchIndex } from '@/features/trades/hooks/trade-form/useInventorySearchIndex';
import type { SearchItem } from '@/features/trades/hooks/useCreateTradeForm';
import { trapTabWithin } from '../utils/focus-trap';
import {
  useCreateAdjustment,
  useUpdateAdjustment,
} from '../hooks/use-adjustment-mutations';
import { useCounterpartyTradeOptions } from '../hooks/use-counterparty-trade-options';
import { formatDate } from '@/utils/format';
import type {
  AdjustmentDirection,
  AdjustmentTradeOption,
  ManualAdjustment,
} from '@/types';

// The popup is addressed by `aria-controls` and its sections are labelled by
// their headers, so the ids have to be stable and shared.
const LIST_ID = 'adjustment-instrument-list';
const INSTRUMENTS_HEADER_ID = 'adjustment-instruments-header';
const TRADES_HEADER_ID = 'adjustment-trades-header';

/** Suppress the mousedown that would blur the search box before the click lands
 *  on the option — the pick would otherwise be dropped in browsers that do not
 *  focus a button on mousedown. */
const keepFocusOnPick = (e: React.MouseEvent) => e.preventDefault();

/** "{n} {unit}(s)" with a thousands separator. `pluralizeUnit` interpolates the
 *  raw number, which renders a five-figure share count as a bare 5000. */
const countLabel = (n: number, unit: string) =>
  `${n.toLocaleString('en-US')} ${n === 1 ? unit : `${unit}s`}`;

/**
 * The recognition line under an option's symbol.
 *
 * Both numbers are final: the server collapses the book to pairs before paging,
 * so `trade_count` and `total_quantity` describe the WHOLE pair and cannot grow
 * as later pages arrive. The size is shown only for a single-trade pair — across
 * several trades a summed quantity mixes open and closed ones and would read as
 * current exposure on a screen about how much money to correct.
 */
const describePair = (option: AdjustmentTradeOption): string => {
  const when = formatDate(option.last_trade_date);
  if (option.trade_count === 1) {
    // Repo legs are cash against collateral, not a share count.
    const unit =
      option.trade_type === 'repo' || option.trade_type === 'reverse_repo'
        ? 'unit'
        : 'share';
    return `${countLabel(option.total_quantity, unit)} · traded ${when}`;
  }
  return `${countLabel(option.trade_count, 'trade')} · latest ${when}`;
};

const sectionHeaderCls =
  'sticky top-0 bg-lr-surface-2 px-3 py-1 text-[11px] font-medium tracking-wide text-lr-fg-2 uppercase';
const optionCls =
  'flex w-full flex-col items-start px-3 py-2 text-left hover:bg-lr-surface-2 focus:bg-lr-surface-2 focus:outline-none';

interface Props {
  counterparty: { id: number; name: string };
  /** Edit mode: an existing unposted adjustment. */
  adjustment?: ManualAdjustment | null;
  /** Reverse mode (сторно): the posted adjustment this one reverses. */
  reverseOf?: ManualAdjustment | null;
  onClose: () => void;
}

/** Local calendar date as `YYYY-MM-DD` — what the trader means by "today". */
const localToday = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

// Positive money grammar (no sign — direction is the toggle): digits, up to 2dp.
const AMOUNT_VALID = /^\d+(\.\d{1,2})?$/;
const AMOUNT_INTERMEDIATE = /^\d*\.?\d{0,2}$/;
// Same corridor the backend enforces (MAX_ADJUSTMENT_AMOUNT), in minor units so
// the comparison never touches a float: a hand-typed correction above this is a
// fat finger, and beyond ~15 significant digits the Back Office wire would carry
// a different number anyway.
const MAX_AMOUNT_MINOR = 100_000_000_000n; // 1e9 major units

/** Whole positive money string -> minor units, or null if it isn't one. */
const toMinorUnits = (value: string): bigint | null => {
  if (!AMOUNT_VALID.test(value)) return null;
  const [whole, fraction = ''] = value.split('.');
  return BigInt(whole + fraction.padEnd(2, '0'));
};

const opposite = (d: AdjustmentDirection): AdjustmentDirection =>
  d === 'to_us' ? 'to_cp' : 'to_us';

/**
 * Create / edit / reverse a manual billing adjustment (LR-563). Direction is a
 * two-button toggle, not a typed minus — on money that guards against a sign
 * flip; the backend derives the stored sign from it. The amount stays a Decimal
 * STRING end-to-end (no float round-trip). Comment is required.
 *
 * The instrument dropdown offers the counterparty's own book first — the
 * (instrument, trade type) pairs it actually has trades on — and picking one
 * fills the instrument, the trade type and the currency together. That is the
 * product's "выбираем какую сделку зааджастить" (2026-07-21) expressed as a
 * pick the storage model can honour, since an adjustment is booked by
 * instrument + trade type rather than against a trade id.
 *
 * Below it sits the same inventory search as the Create Trade form
 * (`useInventorySearchIndex`): type symbol / ISIN / SEDOL / name. And a symbol
 * in neither list is still bookable as typed — reconciliation regularly turns up
 * a break on something we never booked, which is the whole reason adjustments
 * are entered by hand.
 *
 * Edit is a full-form replace of an UNPOSTED adjustment; reverse creates a NEW
 * adjustment pointing back at a posted one (the original is never overwritten).
 */
export const ManualAdjustmentFormModal: React.FC<Props> = ({
  counterparty,
  adjustment,
  reverseOf,
  onClose,
}) => {
  const mode: 'create' | 'edit' | 'reverse' = adjustment
    ? 'edit'
    : reverseOf
      ? 'reverse'
      : 'create';
  const seed = adjustment ?? reverseOf ?? null;
  // A сторно is defined by what it offsets: same booking key (instrument +
  // trade type), same currency, opposite direction — all enforced by the
  // backend. Locking them here rather than leaving them editable into a
  // guaranteed 400 also means neither picker may quietly adopt an instrument's
  // currency.
  //
  // It covers EDITING an existing unposted reversal too, not just the create
  // form: that row is as much a сторно as the one being drafted, and it is the
  // path a person actually returns to. Leaving it open is how the same fields
  // the create form refuses to move become movable one screen later.
  const offsetFixed = mode === 'reverse' || Boolean(adjustment?.reverses);

  const [symbolId, setSymbolId] = useState(seed?.symbol_id ?? '');
  const [showDropdown, setShowDropdown] = useState(false);
  const [tradeType, setTradeType] = useState(seed?.trade_type ?? 'lend');
  const [direction, setDirection] = useState<AdjustmentDirection>(
    adjustment?.direction ??
      (reverseOf ? opposite(reverseOf.direction) : 'to_us')
  );
  const [amount, setAmount] = useState(seed?.abs_amount ?? '');
  const [currency, setCurrency] = useState(seed?.currency ?? 'USD');
  // The Value Date is the NEW transaction's booking date, so a сторно defaults to
  // today rather than inheriting the original's — back-valuing it would post the
  // correction into whatever period the original landed in.
  const [valueDate, setValueDate] = useState(
    adjustment?.value_date ?? localToday()
  );
  // Reverse starts with a blank comment: the reason for the сторно is its own.
  const [comment, setComment] = useState(adjustment?.comment ?? '');

  const dialogRef = useRef<HTMLDivElement>(null);
  const searchBoxRef = useRef<HTMLLabelElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const mouseDownOnBackdrop = useRef(false);
  const createMut = useCreateAdjustment();
  const updateMut = useUpdateAdjustment();
  const active = mode === 'edit' ? updateMut : createMut;

  // Same inventory search index the Create Trade dropdown is built on (LR-495
  // extracted it as a reusable hook). Filters an in-memory index by the typed
  // query; the fetch itself is shared/cached across the app.
  const { filteredInventoryItems, isDataLoading } =
    useInventorySearchIndex(symbolId);
  // The counterparty's own book, collapsed to instrument + trade-type pairs.
  // Searched and paged on the SERVER — see the hook: a 32k-trade counterparty
  // has no client-side answer.
  const {
    tradeOptions,
    isTradesLoading,
    isSearchPending,
    loadMoreTrades,
    hasMoreTrades,
    isLoadingMoreTrades,
    loadedTradeCount,
    totalTradeCount,
  } = useCounterpartyTradeOptions(
    counterparty.id,
    symbolId,
    showDropdown && !offsetFixed
  );
  const query = symbolId.trim();

  // The re-entry guard is a ref, not `isLoadingMoreTrades`: that flag only flips
  // after a re-render, while scroll events arrive several per frame, so the
  // render-state version fires `fetchNextPage` two or three times. Each defaults
  // to `cancelRefetch: true`, which aborts the in-flight page and reissues the
  // SAME offset — a fling can keep restarting one page.
  const fetchingMoreRef = useRef(false);
  const requestMoreTrades = () => {
    if (!hasMoreTrades || isLoadingMoreTrades || fetchingMoreRef.current)
      return;
    fetchingMoreRef.current = true;
    loadMoreTrades({ cancelRefetch: false }).finally(() => {
      fetchingMoreRef.current = false;
    });
  };
  const handleDropdownScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
      requestMoreTrades();
    }
  };
  // Nothing left to pick from: say why (an empty book reads differently from a
  // query that matched nothing) rather than showing a bare empty dropdown. The
  // inventory index only searches once something is typed, so its loading state
  // only counts then.
  const nothingToOffer =
    !isTradesLoading &&
    !isSearchPending &&
    !(query !== '' && isDataLoading) &&
    tradeOptions.length === 0 &&
    filteredInventoryItems.length === 0;

  // One flat, ordered list behind both sections: arrow keys have to cross the
  // Instruments/Trades boundary, and `aria-activedescendant` needs a stable id
  // per option.
  const inventoryEntries = useMemo(
    () =>
      filteredInventoryItems.map((si, idx) => ({
        id: `${LIST_ID}-inv-${idx}`,
        si,
      })),
    [filteredInventoryItems]
  );
  const tradeEntries = useMemo(
    () =>
      tradeOptions.map((option) => ({
        id: `${LIST_ID}-trade-${option.symbol_id}-${option.trade_type}`,
        option,
      })),
    [tradeOptions]
  );
  const [activeOptionId, setActiveOptionId] = useState<string | null>(null);
  // Data, not closures: a memo over the select handlers would rebuild on every
  // render and reset the highlighted option on each keystroke.
  const flatEntries = useMemo(
    () => [
      ...inventoryEntries.map((e) => ({
        id: e.id,
        kind: 'inventory' as const,
        si: e.si,
      })),
      ...tradeEntries.map((e) => ({
        id: e.id,
        kind: 'trade' as const,
        option: e.option,
      })),
    ],
    [inventoryEntries, tradeEntries]
  );
  const activeIndex = flatEntries.findIndex((e) => e.id === activeOptionId);
  const activeOption = activeIndex >= 0 ? flatEntries[activeIndex] : undefined;

  // The highlighted option must exist in the list currently on screen: a new
  // query, or a page that regrouped the pairs, invalidates the old id.
  useEffect(() => {
    setActiveOptionId((current) =>
      current && flatEntries.some((e) => e.id === current) ? current : null
    );
  }, [flatEntries]);

  const moveActive = (delta: number) => {
    if (flatEntries.length === 0) return;
    const next =
      activeIndex < 0
        ? delta > 0
          ? 0
          : flatEntries.length - 1
        : Math.min(Math.max(activeIndex + delta, 0), flatEntries.length - 1);
    setActiveOptionId(flatEntries[next].id);
    document.getElementById(flatEntries[next].id)?.scrollIntoView?.({
      block: 'nearest',
    });
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape' && showDropdown) {
      // Swallowed so it closes the list rather than the dialog — which would
      // discard a half-filled adjustment with no confirmation.
      e.stopPropagation();
      setShowDropdown(false);
      setActiveOptionId(null);
      return;
    }
    if (!showDropdown) {
      if (e.key === 'ArrowDown') setShowDropdown(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveActive(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveActive(-1);
    } else if (e.key === 'Home' && flatEntries.length > 0) {
      e.preventDefault();
      moveActive(-flatEntries.length);
    } else if (e.key === 'End' && flatEntries.length > 0) {
      e.preventDefault();
      moveActive(flatEntries.length);
    } else if (e.key === 'Enter' && activeOption) {
      e.preventDefault();
      if (activeOption.kind === 'inventory') selectSecurity(activeOption.si);
      else selectTradeOption(activeOption.option);
      setActiveOptionId(null);
    }
  };

  const currencyOptions = useMemo(
    () => getSupportedCurrencies(currency ? [currency] : []),
    [currency]
  );

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => opener?.focus?.();
  }, []);

  // Grabbing the dropdown's SCROLLBAR (or any non-focusable part of the box)
  // blurs the input without moving focus to anything: `relatedTarget` is null,
  // which the blur handler would otherwise read as "focus left the box" and
  // close the list mid-drag. The flag says a pointer press started inside, so
  // that blur is not an exit; it clears on the mouseup that ends the gesture.
  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (!searchBoxRef.current?.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  // Adopting a currency from a pick is skipped on a сторно: its currency field
  // is disabled to keep the offset valid, so a silent change there would submit
  // a currency the user can neither see chosen nor undo, into a certain 400.
  const adoptCurrency = (ccy?: string) => {
    if (ccy && !offsetFixed) setCurrency(ccy.toUpperCase());
  };

  const selectSecurity = (si: SearchItem) => {
    if (offsetFixed) return;
    setSymbolId(si.item.symbolId);
    // Match Create Trade: adopt the instrument's currency on pick (still
    // editable). A typed, off-inventory symbol leaves the current currency.
    // Upper-cased to match the option values, or the <select> would show one
    // currency while submitting another.
    adoptCurrency(si.item.currency);
    setShowDropdown(false);
  };

  // Picking off the counterparty's book fills the whole booking key at once:
  // instrument AND trade type (plus the currency), so the operator never has to
  // recall which side the trade was on. Refused outright on a сторно, whose key
  // belongs to the adjustment it offsets — the dropdown is not rendered there,
  // and this is the second lock rather than the only one.
  const selectTradeOption = (option: AdjustmentTradeOption) => {
    if (offsetFixed) return;
    setSymbolId(option.symbol_id);
    setTradeType(option.trade_type);
    adoptCurrency(option.currency);
    setShowDropdown(false);
  };

  const amountMinor = toMinorUnits(amount.trim());
  // Zero is not a correction; the backend's own floor is 0.01.
  const amountValid =
    amountMinor !== null && amountMinor > 0n && amountMinor <= MAX_AMOUNT_MINOR;
  const canSubmit =
    symbolId.trim() !== '' &&
    amountValid &&
    currency !== '' &&
    valueDate !== '' &&
    comment.trim() !== '' &&
    !active.isPending;

  const failure = active.error as {
    response?: { status?: number; data?: Record<string, string[] | string> };
  } | null;
  const errorStatus = failure?.response?.status;
  // Surface the server's field-level reason. The write serializer answers with
  // precise, actionable messages ("A reversal must be in the same currency…");
  // collapsing them into "check the fields" tells the trader to check fields that
  // already passed every client-side rule.
  const fieldErrors = failure?.response?.data;
  const fieldDetail =
    fieldErrors && typeof fieldErrors === 'object'
      ? Object.entries(fieldErrors)
          .map(([f, msgs]) => `${f}: ${Array.isArray(msgs) ? msgs[0] : msgs}`)
          .join('; ')
      : null;
  const errorMessage = active.error
    ? errorStatus === 403
      ? 'You do not have permission to book adjustments.'
      : errorStatus === 409
        ? 'This adjustment was already posted and can no longer be edited.'
        : (fieldDetail ??
          'Failed to save the adjustment. Check the fields and try again.')
    : null;

  const handleSubmit = () => {
    if (!canSubmit) return;
    const params = {
      counterparty: counterparty.id,
      symbol_id: symbolId.trim(),
      trade_type: tradeType,
      direction,
      amount: amount.trim(),
      currency,
      value_date: valueDate,
      comment: comment.trim(),
      ...(mode === 'reverse' && reverseOf ? { reverses: reverseOf.id } : {}),
    };
    if (mode === 'edit' && adjustment) {
      updateMut.mutate({ id: adjustment.id, params }, { onSuccess: onClose });
    } else {
      createMut.mutate(params, { onSuccess: onClose });
    }
  };

  const title =
    mode === 'edit'
      ? 'Edit adjustment'
      : mode === 'reverse'
        ? 'Reverse adjustment'
        : 'New adjustment';

  const inputCls =
    'rounded-md border border-lr-border px-2 py-1.5 text-sm text-lr-fg focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';

  return (
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        mouseDownOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (mouseDownOnBackdrop.current && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="w-full max-w-[456px] rounded-[10px] bg-lr-surface shadow-xl focus:outline-none"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
          trapTabWithin(dialogRef.current, e);
        }}
      >
        <div className="p-6">
          <div className="mb-1 text-[15px] font-semibold text-lr-fg">
            {title}
          </div>
          <p className="mb-4 text-sm leading-relaxed text-lr-muted">
            One-line correction for{' '}
            <b className="text-lr-fg">{counterparty.name}</b>, agreed outside
            the system. It posts to the Back Office and does not change any
            statement.
          </p>

          <div className="mb-4 flex flex-col gap-3">
            {/* The lock hint is a sibling of the label, not a child: a second
                text node inside it becomes part of the label's accessible name
                ("InstrumentLocked to…"), which detaches the field from its
                name for anything that looks the field up by label. */}
            <div className="flex flex-col gap-1">
              <label
                ref={searchBoxRef}
                className="relative flex flex-col gap-1"
                // Leaving the box by keyboard closes the list so it stops covering
                // the fields below. Only a focus-out that actually LANDS
                // somewhere counts: grabbing the popup's scrollbar blurs the
                // input while moving focus nowhere (`relatedTarget` null), and
                // treating that as an exit closed the list mid-drag — a long book
                // could not be scrolled at all. A Tab always names where it went,
                // so the keyboard case stays covered, and a press outside is the
                // document handler's job.
                //
                // Focus is put back because the browser left it on <body> or on
                // the tabIndex={-1} dialog: from there Escape either closes the
                // whole modal and loses the adjustment or does nothing, and Tab
                // walks out past the backdrop (the trap never sees a keydown on
                // body). Deferred so it lands after the browser finishes its own
                // focus assignment for this gesture.
                onBlur={(e) => {
                  const next = e.relatedTarget as Node | null;
                  const box = e.currentTarget;
                  // Focus did not land on another control: either nowhere at all
                  // (`null`), or on an ANCESTOR — pressing a non-focusable spot
                  // such as the popup's scrollbar makes the browser park focus on
                  // the nearest focusable ancestor, which here is the
                  // tabIndex={-1} dialog shell. Neither is the user leaving the
                  // field, and treating either as an exit closes the list the
                  // moment the scrollbar is grabbed.
                  if (!next || next.contains(box)) {
                    // Put focus back, but only while the list is still open: the
                    // document mousedown handler may already have closed it, and
                    // yanking focus back into a closed picker would fight the
                    // click the user actually made.
                    requestAnimationFrame(() => {
                      if (document.getElementById(LIST_ID)) {
                        searchInputRef.current?.focus();
                      }
                    });
                    return;
                  }
                  if (!box.contains(next)) setShowDropdown(false);
                }}
              >
                <span className="text-xs font-medium text-lr-fg-2">
                  Instrument
                </span>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={symbolId}
                  // The booking key of a сторно is the original's. The backend
                  // refuses a reversal that moves the instrument or the trade
                  // type, so the fields are locked rather than left to walk into
                  // a certain 400 — and, before that guard existed, into a
                  // correction posted against something with no break at all.
                  disabled={offsetFixed}
                  onFocus={() => setShowDropdown(true)}
                  // Clicking the already-focused input has to reopen the list:
                  // after Escape there is no focus event left to do it, and the
                  // field would look dead until a character is typed.
                  onMouseDown={() => setShowDropdown(true)}
                  onChange={(e) => {
                    setSymbolId(e.target.value);
                    setShowDropdown(true);
                  }}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Pick a trade, or search by Symbol, ISIN, Name..."
                  autoComplete="off"
                  role="combobox"
                  aria-expanded={showDropdown && !offsetFixed}
                  aria-controls={LIST_ID}
                  aria-activedescendant={activeOption?.id}
                  aria-autocomplete="list"
                  className={clsx(inputCls, 'disabled:cursor-not-allowed')}
                />
                {showDropdown && !offsetFixed && (
                  <div
                    onScroll={handleDropdownScroll}
                    className="absolute left-0 top-full z-20 mt-1 max-h-64 w-full overflow-auto rounded-[10px] border border-lr-border bg-lr-surface shadow-lg"
                  >
                    <div
                      id={LIST_ID}
                      role="listbox"
                      aria-label="Instrument suggestions"
                    >
                      {/* Instruments first: a typed symbol is the fast, bounded
                          answer. The counterparty's book sits under it as the
                          recognition aid, and it is the longer list. */}
                      {inventoryEntries.length > 0 && (
                        <div
                          role="group"
                          aria-labelledby={INSTRUMENTS_HEADER_ID}
                        >
                          <div
                            id={INSTRUMENTS_HEADER_ID}
                            role="presentation"
                            className={sectionHeaderCls}
                          >
                            Instruments
                          </div>
                          {inventoryEntries.map((entry) => (
                            <button
                              key={entry.id}
                              id={entry.id}
                              type="button"
                              role="option"
                              // Options are NOT tab stops: the list runs to 50+
                              // entries, and tabbing through them to reach Trade
                              // type is not a keyboard model. Arrow keys drive it
                              // from the input (`aria-activedescendant`), which is
                              // also why Escape can never be swallowed by an
                              // option that holds focus.
                              tabIndex={-1}
                              aria-selected={entry.id === activeOption?.id}
                              onMouseDown={keepFocusOnPick}
                              onClick={() => selectSecurity(entry.si)}
                              className={clsx(
                                optionCls,
                                entry.id === activeOption?.id &&
                                  'bg-lr-surface-2'
                              )}
                            >
                              <span className="font-mono text-[13px] text-lr-fg">
                                {entry.si.item.symbolId}
                              </span>
                              <span className="text-xs text-lr-muted">
                                {entry.si.item.shortName &&
                                entry.si.item.shortName !==
                                  entry.si.item.symbolId
                                  ? entry.si.item.shortName
                                  : ''}
                                {entry.si.item.isin
                                  ? ` · ${entry.si.item.isin}`
                                  : ''}
                                {entry.si.item.sedol
                                  ? ` · ${entry.si.item.sedol}`
                                  : ''}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}

                      {tradeEntries.length > 0 && (
                        <div role="group" aria-labelledby={TRADES_HEADER_ID}>
                          <div
                            id={TRADES_HEADER_ID}
                            role="presentation"
                            className={sectionHeaderCls}
                          >
                            Trades with {counterparty.name}
                          </div>
                          {tradeEntries.map((entry) => (
                            <button
                              key={entry.id}
                              id={entry.id}
                              type="button"
                              role="option"
                              tabIndex={-1}
                              aria-selected={entry.id === activeOption?.id}
                              onMouseDown={keepFocusOnPick}
                              onClick={() => selectTradeOption(entry.option)}
                              className={clsx(
                                optionCls,
                                entry.id === activeOption?.id &&
                                  'bg-lr-surface-2'
                              )}
                            >
                              <span className="flex w-full items-baseline justify-between gap-2">
                                <span className="font-mono text-[13px] text-lr-fg">
                                  {entry.option.symbol_id}
                                </span>
                                <span className="shrink-0 rounded bg-lr-surface-2 px-1.5 py-0.5 text-[11px] text-lr-fg-2">
                                  {entry.option.trade_type_display}
                                </span>
                              </span>
                              <span className="text-xs text-lr-muted">
                                {entry.option.description &&
                                entry.option.description !==
                                  entry.option.symbol_id
                                  ? `${entry.option.description} · `
                                  : ''}
                                {entry.option.currency}
                                {' · '}
                                {describePair(entry.option)}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Status lines sit OUTSIDE the listbox: they are not options,
                        and a screen reader walking the list must not meet them.
                        They are announced instead — "matched nothing, it will be
                        booked as typed" is the most consequential thing this
                        dropdown says, and it was previously silent. */}
                    <div role="status" aria-live="polite" aria-atomic="true">
                      {(isTradesLoading || isSearchPending) && (
                        <div className="px-3 py-6 text-center text-xs text-lr-muted">
                          {isTradesLoading ? 'Loading trades…' : 'Searching…'}
                        </div>
                      )}

                      {query !== '' && isDataLoading && (
                        <div className="px-3 py-6 text-center text-xs text-lr-muted">
                          Loading inventory…
                        </div>
                      )}

                      {nothingToOffer && (
                        <div className="px-3 py-6 text-center text-xs text-lr-muted">
                          {query === ''
                            ? `No trades booked with ${counterparty.name} — type a symbol to search instruments.`
                            : 'No matching trade or instrument - it will be booked as typed.'}
                        </div>
                      )}
                    </div>

                    {/* Scroll advances the list, but the control is explicit as
                        well: a dropdown that only pages on a wheel gesture is
                        unreachable from the keyboard, and the count has to be
                        visible or a partial list reads as the whole book. */}
                    {hasMoreTrades && (
                      <button
                        type="button"
                        onMouseDown={keepFocusOnPick}
                        onClick={requestMoreTrades}
                        disabled={isLoadingMoreTrades}
                        className="w-full border-t border-lr-border-soft px-3 py-1.5 text-left text-[11px] text-lr-muted hover:bg-lr-surface-2 disabled:cursor-not-allowed"
                      >
                        {/* "pairs", not "instruments": the unit of this list is
                            instrument + leg, so one symbol traded both ways is
                            two rows — and the server's `count` counts the same
                            thing. */}
                        {isLoadingMoreTrades
                          ? 'Loading more…'
                          : `Load more — ${countLabel(loadedTradeCount, 'pair')} of ${
                              totalTradeCount?.toLocaleString('en-US') ?? '…'
                            }. Type a symbol to narrow the search.`}
                      </button>
                    )}
                  </div>
                )}
              </label>
              {offsetFixed && (
                <span className="text-xs text-lr-muted">
                  Locked to the adjustment being reversed.
                </span>
              )}
            </div>

            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-lr-fg-2">
                Trade type
              </span>
              <select
                value={tradeType}
                disabled={offsetFixed}
                onChange={(e) =>
                  setTradeType(e.target.value as typeof tradeType)
                }
                className={clsx(inputCls, 'disabled:cursor-not-allowed')}
              >
                {Object.entries(TRADE_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>

            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-lr-fg-2">
                Direction
              </span>
              <div
                role="group"
                aria-label="Direction"
                className="flex overflow-hidden rounded-md border border-lr-border text-sm"
              >
                <button
                  type="button"
                  aria-pressed={direction === 'to_us'}
                  disabled={offsetFixed}
                  onClick={() => setDirection('to_us')}
                  className={clsx(
                    'flex-1 px-3 py-1.5 disabled:cursor-not-allowed',
                    direction === 'to_us'
                      ? 'bg-primary-600 text-white'
                      : 'bg-lr-surface text-lr-fg-2 enabled:hover:bg-lr-surface-2'
                  )}
                >
                  Due to us
                </button>
                <button
                  type="button"
                  aria-pressed={direction === 'to_cp'}
                  disabled={offsetFixed}
                  onClick={() => setDirection('to_cp')}
                  className={clsx(
                    'flex-1 border-l border-lr-border px-3 py-1.5 disabled:cursor-not-allowed',
                    direction === 'to_cp'
                      ? 'bg-primary-600 text-white'
                      : 'bg-lr-surface text-lr-fg-2 enabled:hover:bg-lr-surface-2'
                  )}
                >
                  Due to counterparty
                </button>
              </div>
              {offsetFixed && (
                <span className="text-xs text-lr-muted">
                  A reversal offsets the original: same instrument, trade type
                  and currency, opposite direction.
                </span>
              )}
            </div>

            <div className="grid grid-cols-[1fr_120px] gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-lr-fg-2">Amount</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/\s/g, '');
                    const v = raw.includes('.') ? raw.replace(/,/g, '') : raw;
                    if (AMOUNT_INTERMEDIATE.test(v)) setAmount(v);
                  }}
                  aria-invalid={amount !== '' && !amountValid}
                  placeholder="0.00"
                  className={clsx(inputCls, 'tabular-nums')}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-lr-fg-2">
                  Currency
                </span>
                <select
                  value={currency}
                  disabled={offsetFixed}
                  onChange={(e) => setCurrency(e.target.value)}
                  className={clsx(inputCls, 'disabled:cursor-not-allowed')}
                >
                  {currencyOptions.map((ccy) => (
                    <option key={ccy} value={ccy}>
                      {ccy}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-lr-fg-2">
                Value date
              </span>
              <input
                type="date"
                value={valueDate}
                onChange={(e) => setValueDate(e.target.value)}
                className={inputCls}
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-lr-fg-2">
                Comment <span className="text-lr-neg">*</span>
              </span>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={2}
                placeholder="Reason for the adjustment"
                className={clsx(inputCls, 'resize-none')}
              />
            </label>
          </div>

          {errorMessage && (
            <div className="mb-4 text-xs text-lr-neg">{errorMessage}</div>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-lr-border bg-lr-surface px-4 py-2 text-sm font-medium text-lr-fg-2 hover:bg-lr-surface-2"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {active.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
